/**
 * Pinochle — server-authoritative rules engine (four-handed partnership auction pinochle).
 *
 * Rules implemented (toggles live in {@link PinochleConfig}):
 *  - 48 cards: A 10 K Q J 9 in each suit, two of each. Seats 0 & 2 (team 0) play seats 1 & 3 (team 1). Twelve cards each.
 *  - Auction: starting left of the dealer, each player passes (and is out) or bids higher than the last bid. The
 *    dealer must bid the minimum when everyone else passed. The last bidder left is the declarer and names the trump.
 *  - Passing (optional): the declarer's partner hands three cards to the declarer, who hands three back.
 *  - Meld: every player's hand is scored automatically for the trump (runs, marriages, dix, pinochle, "around" sets —
 *    see meld.ts) and shown to the table.
 *  - Play: the declarer leads. Players must follow suit, and must beat the winning card when they can; with no card of
 *    the suit they must trump (and over-trump) if they can. Trick order is A 10 K Q J 9; the first of two equal
 *    cards wins. Aces, tens and kings are counters worth one point each, and the last trick is worth one more.
 *  - Scoring: a team scores its meld plus its counters, but only if it took at least one trick. The declarer's team
 *    must reach the bid or it loses the bid instead. The match ends when a team reaches `targetScore`; if both do in
 *    the same deal, the declarer's team wins. Partners share a place.
 */
import { buildDeck, parseCard, removeCards, sortHand, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { checkBool, checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';
import { pinochleMeld, type PinochleMeld } from './meld.js';

export interface PinochleConfig {
  targetScore: number;
  maxRounds: number;
  minBid: number;
  /** The declarer's partner passes three cards across and the declarer three back. */
  passCards: boolean;
  roundEndDelayMs: number;
  trickEndDelayMs: number;
  meldDelayMs: number;
}

export type PinochlePhase = 'BIDDING' | 'TRUMP' | 'PASS_PARTNER' | 'PASS_DECLARER' | 'MELD' | 'PLAYING' | 'TRICK_END' | 'ROUND_END' | 'MATCH_END';

export interface PinochleTrickPlay {
  seat: number;
  card: CardId;
}

export interface PinochleCompletedTrick {
  leader: number;
  plays: PinochleTrickPlay[];
  winner: number;
  counters: number;
}

export interface PinochleTeamRound {
  declarer: boolean;
  bid: number;
  meld: number;
  counters: number;
  tricks: number;
  /** Meld and counters count only for a team that took a trick. */
  total: number;
  delta: number;
}

export interface PinochleState {
  config: PinochleConfig;
  players: string[];
  round: number;
  dealer: number;
  phase: PinochlePhase;
  hands: CardId[][];
  /** Per seat: null = has not acted yet, 0 = passed, n = bid. */
  bids: (number | null)[];
  highBid: { seat: number; value: number } | null;
  trump: Suit | null;
  /** Cards the declarer's partner passed across, held until the declarer has returned three. */
  passed: CardId[];
  meld: PinochleMeld[];
  tricksWon: number[];
  counters: number[];
  turn: number;
  trick: PinochleTrickPlay[];
  tricks: PinochleCompletedTrick[];
  teamScores: number[];
  roundResults: PinochleTeamRound[][];
}

export type PinochleAction =
  | { type: 'bid'; value: number }
  | { type: 'pass' }
  | { type: 'trump'; suit: Suit }
  | { type: 'passCards'; cards: [CardId, CardId, CardId] }
  | { type: 'play'; card: CardId };

export type PinochleLegal =
  | { kind: 'bid'; min: number; max: number; canPass: boolean }
  | { kind: 'trump'; suits: Suit[] }
  | { kind: 'passCards'; count: 3; role: 'partner' | 'declarer' }
  | { kind: 'play'; cards: CardId[] }
  | { kind: 'none' };

export interface PinochleView {
  gameType: 'PINOCHLE';
  config: PinochleConfig;
  seat: number | null;
  phase: PinochlePhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  bids: (number | null)[];
  highBid: { seat: number; value: number } | null;
  declarer: number | null;
  trump: Suit | null;
  /** Meld of every seat, public once the passing is over. */
  meld: (PinochleMeld | null)[];
  tricksWon: number[];
  counters: number[];
  trick: PinochleTrickPlay[];
  lastTrick: PinochleCompletedTrick | null;
  tricks: PinochleCompletedTrick[];
  teamScores: number[];
  roundResults: PinochleTeamRound[][];
  totals: number[];
}

const DEFAULTS: PinochleConfig = {
  targetScore: 150,
  maxRounds: 12,
  minBid: 20,
  passCards: true,
  roundEndDelayMs: 6000,
  trickEndDelayMs: 900,
  meldDelayMs: 5000,
};

const SEATS = 4;
const MAX_BID = 120;
const TRICKS = 12;

export const pinochleTeamOf = (seat: number): number => seat % 2;
export const pinochlePartnerOf = (seat: number): number => (seat + 2) % SEATS;

const STRENGTH: Record<number, number> = { 14: 6, 10: 5, 13: 4, 12: 3, 11: 2, 9: 1 };
export const pinochleStrength = (card: CardId): number => STRENGTH[parseCard(card).rank]!;
/** Counters: aces, tens and kings, one point each. */
export const pinochleCounter = (card: CardId): number => (STRENGTH[parseCard(card).rank]! >= 4 ? 1 : 0);

export function pinochleDeck(): CardId[] {
  return buildDeck({ decks: 2 }).filter((c) => parseCard(c).rank >= 9);
}

/** Does `later` take the trick from `current` (current being the best card so far)? */
function beats(later: CardId, current: CardId, trump: Suit): boolean {
  const a = parseCard(later);
  const b = parseCard(current);
  if (a.suit === trump && b.suit !== trump) return true;
  if (a.suit !== b.suit) return false;
  return pinochleStrength(later) > pinochleStrength(current);
}

/** The play currently winning (or, for a complete trick, winning) a trick; the first of equal cards wins. */
export function pinochleBestPlay(plays: readonly PinochleTrickPlay[], trump: Suit): PinochleTrickPlay {
  let best = plays[0]!;
  for (const p of plays.slice(1)) if (beats(p.card, best.card, trump)) best = p;
  return best;
}

export const pinochleTrickWinner = (plays: readonly PinochleTrickPlay[], trump: Suit): number => pinochleBestPlay(plays, trump).seat;

/** Follow suit, beat the winning card when able, trump when out of the suit. */
export function pinochleLegalCards(hand: readonly CardId[], trick: readonly PinochleTrickPlay[], trump: Suit): CardId[] {
  if (trick.length === 0) return [...hand];
  const lead = parseCard(trick[0]!.card).suit;
  const best = pinochleBestPlay(trick, trump).card;
  const bestIsTrump = parseCard(best).suit === trump;
  const followers = hand.filter((c) => parseCard(c).suit === lead);
  if (followers.length > 0) {
    if (bestIsTrump && lead !== trump) return followers;
    const higher = followers.filter((c) => beats(c, best, trump));
    return higher.length > 0 ? higher : followers;
  }
  const trumps = hand.filter((c) => parseCard(c).suit === trump);
  if (trumps.length > 0) {
    if (bestIsTrump) {
      const over = trumps.filter((c) => beats(c, best, trump));
      return over.length > 0 ? over : trumps;
    }
    return trumps;
  }
  return [...hand];
}

/** The bid range open to the seat to act. */
export function pinochleBidRange(state: Pick<PinochleState, 'config' | 'highBid' | 'bids'>, _seat: number): { min: number; max: number; canPass: boolean } {
  const min = Math.max(state.config.minBid, (state.highBid?.value ?? 0) + 1);
  // The last player left without a bid on the table (the dealer) must open.
  const remaining = state.bids.filter((b) => b !== 0).length;
  return { min, max: MAX_BID, canPass: !(state.highBid === null && remaining === 1) };
}

/** Suit with the most cards, favouring a long suit of strong cards — the default trump pick. */
export function pinochleBestSuit(hand: readonly CardId[]): Suit {
  let best: Suit = SUITS[0]!;
  let bestScore = -1;
  for (const suit of SUITS) {
    const cards = hand.filter((c) => parseCard(c).suit === suit);
    const score = cards.length * 10 + cards.reduce((n, c) => n + pinochleStrength(c), 0) + pinochleMeld(hand, suit).points * 0.5;
    if (score > bestScore) {
      bestScore = score;
      best = suit;
    }
  }
  return best;
}

/** How much a card is worth to its holder when the trump is set (used to choose passes and by the bot). */
export function pinochleCardValue(card: CardId, trump: Suit): number {
  const p = parseCard(card);
  return pinochleStrength(card) * 2 + (p.suit === trump ? 5 : 0) + pinochleCounter(card);
}

function deal(state: PinochleState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(pinochleDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  deck.forEach((c, i) => hands[i % SEATS]!.push(c));
  state.hands = hands.map((h) => sortHand(h));
  state.bids = [null, null, null, null];
  state.highBid = null;
  state.trump = null;
  state.passed = [];
  state.meld = [];
  state.tricksWon = [0, 0];
  state.counters = [0, 0];
  state.trick = [];
  state.tricks = [];
  state.phase = 'BIDDING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, counts: state.hands.map((h) => h.length) });
}

/** The next seat that has not passed, going clockwise from `seat`. */
function nextBidder(state: PinochleState, seat: number): number {
  for (let i = 1; i <= SEATS; i++) {
    const s = (seat + i) % SEATS;
    if (state.bids[s] !== 0) return s;
  }
  return seat;
}

function beginMeld(state: PinochleState, events: EngineEvent[]): void {
  const trump = state.trump!;
  state.meld = state.hands.map((h) => pinochleMeld(h, trump));
  state.phase = 'MELD';
  events.push({ type: 'meld', points: state.meld.map((m) => m.points) });
}

export const pinochleEngine: GameEngine<PinochleConfig, PinochleState, PinochleAction, PinochleView, PinochleLegal> = {
  gameType: 'PINOCHLE',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 50, 1000);
    checkInt('maxRounds', c.maxRounds, 1, 40);
    checkInt('minBid', c.minBid, 15, 60);
    checkBool('passCards', c.passCards);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    checkInt('meldDelayMs', c.meldDelayMs, 0, 60000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Pinochle needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: PinochleState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      phase: 'BIDDING',
      hands: [],
      bids: [],
      highBid: null,
      trump: null,
      passed: [],
      meld: [],
      tricksWon: [0, 0],
      counters: [0, 0],
      turn: 0,
      trick: [],
      tricks: [],
      teamScores: [0, 0],
      roundResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'bid' && typeof raw.value === 'number' && Number.isInteger(raw.value)) return { type: 'bid', value: raw.value };
    if (raw.type === 'pass') return { type: 'pass' };
    if (raw.type === 'trump' && typeof raw.suit === 'string' && (SUITS as readonly string[]).includes(raw.suit)) return { type: 'trump', suit: raw.suit as Suit };
    if (raw.type === 'passCards' && Array.isArray(raw.cards) && raw.cards.length === 3 && raw.cards.every((c) => typeof c === 'string' && c.length <= 6)) {
      return { type: 'passCards', cards: [raw.cards[0] as string, raw.cards[1] as string, raw.cards[2] as string] };
    }
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 6) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Pinochle action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    const acting: PinochlePhase[] = ['BIDDING', 'TRUMP', 'PASS_PARTNER', 'PASS_DECLARER', 'PLAYING'];
    assertEngine(acting.includes(state.phase) && seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (action.type === 'bid' || action.type === 'pass') {
      assertEngine(state.phase === 'BIDDING', 'INVALID_PHASE', 'Bidding is closed');
      const range = pinochleBidRange(state, seat);
      if (action.type === 'pass') {
        assertEngine(range.canPass, 'ILLEGAL_MOVE', 'You must open the bidding');
        state.bids[seat] = 0;
        events.push({ type: 'pass', seat });
      } else {
        assertEngine(action.value >= range.min && action.value <= range.max, 'INVALID_AMOUNT', 'Bid out of range');
        state.bids[seat] = action.value;
        state.highBid = { seat, value: action.value };
        events.push({ type: 'bid', seat, value: action.value });
      }
      const remaining = state.bids.filter((b) => b !== 0).length;
      if (remaining === 1 && state.highBid) {
        const winner = state.highBid;
        state.phase = 'TRUMP';
        state.turn = winner.seat;
        events.push({ type: 'biddingComplete', declarer: winner.seat, bid: winner.value });
      } else {
        state.turn = nextBidder(state, seat);
      }
      return events;
    }

    if (action.type === 'trump') {
      assertEngine(state.phase === 'TRUMP', 'INVALID_PHASE', 'Trump is not being chosen');
      state.trump = action.suit;
      events.push({ type: 'trumpChosen', seat, suit: action.suit });
      if (state.config.passCards) {
        state.phase = 'PASS_PARTNER';
        state.turn = pinochlePartnerOf(seat);
      } else {
        beginMeld(state, events);
      }
      return events;
    }

    if (action.type === 'passCards') {
      assertEngine(state.phase === 'PASS_PARTNER' || state.phase === 'PASS_DECLARER', 'INVALID_PHASE', 'Not passing cards now');
      const hand = state.hands[seat]!;
      assertEngine(new Set(action.cards).size === 3 && action.cards.every((c) => hand.includes(c)), 'INVALID_CARD', 'Choose three different cards from your hand');
      const declarer = state.highBid!.seat;
      removeCards(hand, action.cards);
      if (state.phase === 'PASS_PARTNER') {
        state.passed = [...action.cards];
        state.hands[declarer] = sortHand([...state.hands[declarer]!, ...action.cards]);
        state.phase = 'PASS_DECLARER';
        state.turn = declarer;
        events.push({ type: 'passedCards', seat, count: 3 });
      } else {
        const partner = pinochlePartnerOf(declarer);
        state.hands[partner] = sortHand([...state.hands[partner]!, ...action.cards]);
        state.passed = [];
        events.push({ type: 'passedCards', seat, count: 3 });
        beginMeld(state, events);
      }
      return events;
    }

    assertEngine(state.phase === 'PLAYING', 'INVALID_PHASE', 'Not in play phase');
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(pinochleLegalCards(hand, state.trick, state.trump!).includes(action.card), 'ILLEGAL_MOVE', 'That card is not allowed now');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = pinochleTrickWinner(state.trick, state.trump!);
    const counters = state.trick.reduce((n, p) => n + pinochleCounter(p.card), 0) + (state.tricks.length === TRICKS - 1 ? 1 : 0);
    const team = pinochleTeamOf(winner);
    state.tricksWon[team]!++;
    state.counters[team]! += counters;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner, counters });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length, counters });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    const acting: PinochlePhase[] = ['BIDDING', 'TRUMP', 'PASS_PARTNER', 'PASS_DECLARER', 'PLAYING'];
    return acting.includes(state.phase) ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    switch (state.phase) {
      case 'BIDDING':
        return { kind: 'bid', ...pinochleBidRange(state, seat) };
      case 'TRUMP':
        return { kind: 'trump', suits: [...SUITS] };
      case 'PASS_PARTNER':
        return { kind: 'passCards', count: 3, role: 'partner' };
      case 'PASS_DECLARER':
        return { kind: 'passCards', count: 3, role: 'declarer' };
      case 'PLAYING':
        return { kind: 'play', cards: pinochleLegalCards(state.hands[seat]!, state.trick, state.trump!) };
      default:
        return { kind: 'none' };
    }
  },

  getTimeoutAction(state, seat) {
    const hand = state.hands[seat]!;
    if (state.phase === 'BIDDING') {
      const range = pinochleBidRange(state, seat);
      return range.canPass ? { type: 'pass' } : { type: 'bid', value: range.min };
    }
    if (state.phase === 'TRUMP') return { type: 'trump', suit: pinochleBestSuit(hand) };
    if (state.phase === 'PASS_PARTNER' || state.phase === 'PASS_DECLARER') {
      const trump = state.trump!;
      const ranked = [...hand].sort((a, b) => pinochleCardValue(b, trump) - pinochleCardValue(a, trump));
      // The partner sends its best cards across; the declarer sends its worst back.
      const picks = state.phase === 'PASS_PARTNER' ? ranked.slice(0, 3) : ranked.slice(-3);
      return { type: 'passCards', cards: [picks[0]!, picks[1]!, picks[2]!] };
    }
    const legal = pinochleLegalCards(hand, state.trick, state.trump!);
    const lowest = [...legal].sort((a, b) => pinochleCardValue(a, state.trump!) - pinochleCardValue(b, state.trump!))[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    const meldShown = state.phase === 'MELD' || state.phase === 'PLAYING' || state.phase === 'TRICK_END' || state.phase === 'ROUND_END' || state.phase === 'MATCH_END';
    return {
      gameType: 'PINOCHLE',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      bids: [...state.bids],
      highBid: state.highBid ? { ...state.highBid } : null,
      declarer: state.phase === 'BIDDING' ? null : (state.highBid?.seat ?? null),
      trump: state.trump,
      meld: meldShown && state.meld.length > 0 ? state.meld.map((m) => ({ points: m.points, items: m.items.map((i) => ({ ...i, cards: [...i.cards] })) })) : [null, null, null, null],
      tricksWon: [...state.tricksWon],
      counters: [...state.counters],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      teamScores: [...state.teamScores],
      roundResults: state.roundResults.map((r) => r.map((x) => ({ ...x }))),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[pinochleTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'MELD') return { kind: 'meld', delayMs: state.config.meldDelayMs };
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'ROUND_END') return { kind: 'nextRound', delayMs: state.config.roundEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'MELD') {
      state.phase = 'PLAYING';
      state.turn = state.highBid!.seat;
      return events;
    }
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < TRICKS) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const cfg = state.config;
      const { seat: declarerSeat, value: bid } = state.highBid!;
      const declarerTeam = pinochleTeamOf(declarerSeat);
      const results: PinochleTeamRound[] = [0, 1].map((team) => {
        const meld = state.meld[team]!.points + state.meld[team + 2]!.points;
        const counters = state.counters[team]!;
        const tricks = state.tricksWon[team]!;
        const total = tricks > 0 ? meld + counters : 0;
        const declarer = team === declarerTeam;
        const delta = declarer ? (total >= bid ? total : -bid) : total;
        return { declarer, bid, meld, counters, tricks, total, delta };
      });
      results.forEach((r, team) => {
        state.teamScores[team]! += r.delta;
      });
      state.roundResults.push(results);
      events.push({ type: 'roundEnd', round: state.round, results, teamScores: [...state.teamScores] });
      const [a, b] = state.teamScores as [number, number];
      const reached = a >= cfg.targetScore || b >= cfg.targetScore;
      if (reached || state.round >= cfg.maxRounds) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: teamStandings(state) });
      } else {
        state.phase = 'ROUND_END';
      }
      return events;
    }
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};

/**
 * Partners share a place. The higher score wins; when both teams are over the target in the same deal the declarer's
 * team wins; equal scores share first place.
 */
function teamStandings(state: PinochleState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  const target = state.config.targetScore;
  let winner: number | null = a > b ? 0 : b > a ? 1 : null;
  const last = state.roundResults.at(-1);
  if (a >= target && b >= target && last) winner = last[0]!.declarer ? 0 : 1;
  return [0, 1, 2, 3].map((seat) => {
    const team = pinochleTeamOf(seat);
    return { seat, score: team === 0 ? a : b, place: winner === null || winner === team ? 1 : 2 };
  });
}
